"use client"

import * as React from "react"

import type { TileFallback } from "@/features/finance/overview/church-f3/components/church-tile-parts"
import { fallbackPeriod, monthLongName, type FallbackPeriod } from "@/features/finance/overview/church-f3/lib/fallback-period"
import { useFinanceOverviewPeriod } from "@/features/finance/overview/context/finance-overview-context"
import { useLocale, useTranslations } from "@/i18n"

/**
 * The previous comparable period a period-based board tile (F3, F1) falls back to when the board's period has no
 * money yet, and its subtitle label ("Octobre (rien encore en novembre)"). Null while the period is unresolved.
 */
export function usePeriodFallback(): { period: FallbackPeriod | null; fallback: TileFallback | null } {
  const t = useTranslations("finance.overview.church")
  const locale = useLocale()
  const period = useFinanceOverviewPeriod()
  return React.useMemo(() => {
    const previous = period.from && period.to ? fallbackPeriod(period.from, period.to) : null
    const currentMonth = period.from?.slice(0, 7)
    const label = previous
      ? previous.month && currentMonth
        ? t("fallback.month", {
            month: monthLongName(previous.month, locale),
            current: new Intl.DateTimeFormat(locale, { month: "long" }).format(new Date(`${currentMonth}-15T12:00:00`)),
          })
        : t("fallback.period")
      : null
    return { period: previous, fallback: label ? { label } : null }
  }, [period.from, period.to, locale, t])
}
